/**
 * Whether this document is signing out.
 *
 * `Session.logout` clears the credential in place and then assigns `location`, and the old document
 * keeps running until the login page starts to load. In that window every query still mounted can
 * refetch with no token, and every page can re-render over a session with no user. Each of the
 * ~560 places that turn a failure into a snackbar then showed its own red message — "You are not
 * authorized to view this proposal" over a page that was already leaving (#5827).
 *
 * The flag comes down again in the three ways this document can outlive the sign-out:
 *
 * - **A leave-page prompt the person answered "Stay".** A page with unsaved work cancels
 *   `beforeunload` (every editor does, through `useAskBeforeUnload`), and then the browser asks.
 *   Nothing tells the page the answer, and a timer cannot stand in for it: Chromium's prompt does
 *   not stop the page's timers, so a timer cannot tell "Stay" from a slow "Leave". Input can. The
 *   page receives none while the prompt is open, so the first key or pointer press after a
 *   cancelled `beforeunload` proves the person stayed.
 * - **A restore from the back/forward cache**, which brings this same document back to life.
 * - **A new session**, established in this document without a load (`clearSignOutInProgress`).
 *
 * Without these, a document that survived its sign-out hid every later failure for good, and sent
 * none of them to error reporting.
 */
let signOutInProgress = false;
let stopWatchingForSurvival: (() => void) | undefined;

export function markSignOutStarted(): void {
  signOutInProgress = true;
  watchForTheDocumentSurviving();
}

export function isSignOutInProgress(): boolean {
  return signOutInProgress;
}

export function clearSignOutInProgress(): void {
  signOutInProgress = false;
  stopWatchingForSurvival?.();
  stopWatchingForSurvival = undefined;
}

function watchForTheDocumentSurviving(): void {
  if (stopWatchingForSurvival || typeof window === "undefined") return;

  const clearOnInput = () => clearSignOutInProgress();
  const listenForInput = () => {
    window.addEventListener("pointerdown", clearOnInput, { capture: true });
    window.addEventListener("keydown", clearOnInput, { capture: true });
  };
  // Registered as the sign-out starts, so it runs after any handler the page registered earlier
  // and sees whether one of them cancelled the unload.
  // A page cancels either way: `preventDefault()`, or the legacy non-empty `returnValue` string. It
  // has to be a string — `Event.returnValue` is also a legacy boolean that is `true` on every event
  // nobody cancelled.
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    const cancelledByReturnValue = typeof event.returnValue === "string" && event.returnValue !== "";
    if (event.defaultPrevented || cancelledByReturnValue) listenForInput();
  };
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) clearSignOutInProgress();
  };

  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pageshow", onPageShow);
  stopWatchingForSurvival = () => {
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("pointerdown", clearOnInput, { capture: true });
    window.removeEventListener("keydown", clearOnInput, { capture: true });
  };
}
